import React, { useCallback, useEffect, useState } from 'react';
import { Dropdown } from 'antd';
import { Button, ReadOnlyGuard, SparkIcon, Spinner, TooltipTrigger } from '@spark';
import { undeployFirstTooltip } from '@/shared/utils/deploymentUx';
import './DeployDropdown.css';

export const QUERY_FILE_LANGUAGES = [
    { id: 'python', label: 'Python' },
    { id: 'typescript', label: 'TypeScript' },
    { id: 'go', label: 'Go' },
];

/**
 * Deploy dropdown per Figma (file KcQvkthzkvYFRqHHj0hCo4, node 3480:19247):
 * a primary "Deploy ▾" trigger opening a custom panel with two sections —
 *   1. "Versions": every completed version (V{n}); the currently deployed
 *      version carries an inline "Undeploy" tag (arrow-back icon + label,
 *      with an informative tooltip). While a version is deployed the other
 *      rows are disabled with the shared undeploy-first tooltip. While a
 *      deploy/undeploy is in flight (`inProgressAction`) the affected row
 *      and the trigger button show a spinner and the other rows are
 *      blocked; the Undeploy tag appears once the deploy completes.
 *   2. "Query File": Python / TypeScript / Go rows (trailing eye icon) that
 *      open the query-file code modal.
 */
const DeployDropdown = ({
    versions = [],
    deployedVersion = null,
    loading = false,
    disabled = false,
    // 'deploy' | 'undeploy' | null — a request is in flight. The trigger and
    // the affected version row show a spinner until the WebSocket status
    // update resolves it (→ Deployed / Ready to Deploy).
    inProgressAction = null,
    onDeployVersion,
    onUndeploy,
    onOpenQueryFile,
    onOpenChange,
}) => {
    const [open, setOpen] = useState(false);
    // The version whose deploy was just requested — `deployed_version` is
    // only set on the usecase once the deploy completes, so the in-flight
    // row is remembered locally from the click.
    const [pendingVersion, setPendingVersion] = useState(null);

    const isDeploying = inProgressAction === 'deploy';
    const isUndeploying = inProgressAction === 'undeploy';
    const inProgress = isDeploying || isUndeploying;

    useEffect(() => {
        if (!inProgress) setPendingVersion(null);
    }, [inProgress]);

    const handleOpenChange = useCallback(
        (nextOpen) => {
            setOpen(nextOpen);
            onOpenChange?.(nextOpen);
        },
        [onOpenChange]
    );

    const closeAnd = useCallback((action) => {
        setOpen(false);
        action?.();
    }, []);

    const hasDeployed = deployedVersion !== null && deployedVersion !== undefined;
    const deployedVersionNumber = hasDeployed ? Number(deployedVersion) : null;
    const deployedLabel = hasDeployed ? `V${deployedVersionNumber}` : null;

    const renderVersionRow = (version) => {
        const versionNumber = Number(version);
        const isDeployedRow = hasDeployed && versionNumber === deployedVersionNumber;
        const isPendingDeployRow =
            isDeploying &&
            pendingVersion !== null &&
            versionNumber === Number(pendingVersion);

        // The row whose deploy (or undeploy, for the deployed row) is in
        // flight — spinner where the Undeploy tag will appear once done.
        if (isPendingDeployRow || (isDeployedRow && isUndeploying)) {
            return (
                <div
                    key={`version-${version}`}
                    className="deploy-dropdown__item deploy-dropdown__item--static fs-unmask"
                >
                    <span className="deploy-dropdown__item-label">V{versionNumber}</span>
                    <Spinner
                        size="small"
                        aria-label={
                            isPendingDeployRow
                                ? `Deploying V${versionNumber}`
                                : `Undeploying V${versionNumber}`
                        }
                    />
                </div>
            );
        }

        if (isDeployedRow) {
            return (
                <div
                    key={`version-${version}`}
                    className="deploy-dropdown__item deploy-dropdown__item--static fs-unmask"
                >
                    <span className="deploy-dropdown__item-label">V{versionNumber}</span>
                    <ReadOnlyGuard placement="left">
                        <TooltipTrigger
                            text={
                                `V${versionNumber} is currently deployed. Click to undeploy ` +
                                'it and stop serving this model version.'
                            }
                            placement="left"
                        >
                            <button
                                type="button"
                                className="deploy-dropdown__undeploy-tag"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    closeAnd(onUndeploy);
                                }}
                            >
                                <SparkIcon slug="arrow-back-up" size="small" />
                                Undeploy
                            </button>
                        </TooltipTrigger>
                    </ReadOnlyGuard>
                </div>
            );
        }

        if (hasDeployed || inProgress) {
            // Only one version can be deployed per task — block the others
            // (also while a deploy/undeploy request is still in flight).
            const tooltipText = inProgress
                ? 'A deployment change is in progress. Please wait for it to finish.'
                : undeployFirstTooltip(deployedLabel);
            return (
                <TooltipTrigger
                    key={`version-${version}`}
                    text={tooltipText}
                    placement="left"
                    className="deploy-dropdown__item-tooltip"
                >
                    <button
                        type="button"
                        className="deploy-dropdown__item deploy-dropdown__item--disabled fs-unmask"
                        disabled
                    >
                        <span className="deploy-dropdown__item-label">V{versionNumber}</span>
                    </button>
                </TooltipTrigger>
            );
        }

        return (
            <ReadOnlyGuard
                key={`version-${version}`}
                placement="left"
                className="deploy-dropdown__item-tooltip"
            >
                <button
                    type="button"
                    className="deploy-dropdown__item fs-unmask"
                    onClick={() => {
                        setPendingVersion(version);
                        closeAnd(() => onDeployVersion?.(version));
                    }}
                >
                    <span className="deploy-dropdown__item-label">V{versionNumber}</span>
                </button>
            </ReadOnlyGuard>
        );
    };

    const renderVersionsSection = () => {
        if (loading) {
            return <div className="deploy-dropdown__empty">Loading versions...</div>;
        }
        if (versions.length === 0) {
            return <div className="deploy-dropdown__empty">No versions available</div>;
        }
        return versions.map(renderVersionRow);
    };

    const panel = (
        <div className="deploy-dropdown__panel" role="menu">
            <div className="deploy-dropdown__section">
                <div className="deploy-dropdown__section-title fs-unmask">Versions</div>
                <div className="deploy-dropdown__section-items">{renderVersionsSection()}</div>
            </div>
            <div className="deploy-dropdown__section">
                <div className="deploy-dropdown__section-title fs-unmask">Query File</div>
                <div className="deploy-dropdown__section-items">
                    {QUERY_FILE_LANGUAGES.map(({ id, label }) => (
                        <button
                            key={id}
                            type="button"
                            className="deploy-dropdown__item fs-unmask"
                            onClick={() => closeAnd(() => onOpenQueryFile?.(id))}
                        >
                            <span className="deploy-dropdown__item-label">{label}</span>
                            <SparkIcon slug="eye" size="small" className="deploy-dropdown__item-icon" />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <Dropdown
            open={open}
            onOpenChange={handleOpenChange}
            trigger={['click']}
            placement="bottomRight"
            disabled={disabled}
            dropdownRender={() => panel}
        >
            <Button
                variant="primary"
                size="medium"
                type="button"
                disabled={disabled}
                endIcon={
                    inProgress ? (
                        <Spinner
                            size="small"
                            className="deploy-dropdown__trigger-spinner"
                            aria-label="Deployment in progress"
                        />
                    ) : (
                        <SparkIcon slug="chevron-down" size="medium" />
                    )
                }
            >
                Deploy
            </Button>
        </Dropdown>
    );
};

export default DeployDropdown;
